<!--
  Настройка отображения catalog/reference/computed поля перед добавлением на сетку паспорта.
-->
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useTranslate } from '@/shared/lib'
import { VModal, VSqlEditor } from '@/shared/ui'

export type PassportBuilderFieldLayoutMode = 'catalog' | 'reference' | 'computed'
type PassportBuilderFieldLayoutViewType = 'value' | 'table'

const VALUE_ONLY_REFERENCE_CARDINALITIES = new Set(['one_to_one', 'many_to_one'])

export type PassportBuilderFieldLayoutValue = {
  view_type: PassportBuilderFieldLayoutViewType
  pseudo_sql: string
  filter_map: boolean
  filter_search: boolean
  filter_dictionary_id: number | null
}

/** Опция справочника для связи фильтра/поиска с размещением поля. */
export type PassportBuilderFieldLayoutDictionary = {
  id: number
  name: string
}

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    mode: PassportBuilderFieldLayoutMode
    initialValue?: PassportBuilderFieldLayoutValue | null
    relationCardinality?: string | null
    fieldInfo?: string | null
    /** Собственные справочники группы (тот же список, что в модалке «Справочники»). */
    dictionaries?: PassportBuilderFieldLayoutDictionary[]
  }>(),
  {
    initialValue: null,
    relationCardinality: null,
    fieldInfo: null,
    dictionaries: () => [],
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  save: [value: PassportBuilderFieldLayoutValue]
}>()

const { trs } = useTranslate()

const pseudoSql = ref('')
const viewType = ref<PassportBuilderFieldLayoutViewType>('value')
const filterMap = ref(false)
const filterSearch = ref(false)
const filterDictionaryId = ref<number | null>(null)

const open = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const viewTypeItems = computed<Array<{ title: string; value: PassportBuilderFieldLayoutViewType }>>(
  () => [
    { title: trs('itf.value'), value: 'value' },
    { title: trs('itf.table'), value: 'table' },
  ],
)

const isValueOnlyReference = computed(
  () =>
    props.mode === 'reference' &&
    VALUE_ONLY_REFERENCE_CARDINALITIES.has(props.relationCardinality ?? ''),
)

const pseudoSqlHint = computed(() =>
  trs(
    props.mode === 'computed'
      ? 'itf.passport_builder_computed_pseudo_sql_hint'
      : 'itf.passport_builder_pseudo_sql_hint',
  ),
)

/** Эффективный тип отображения: выбор «таблица» доступен только для reference-связей. */
const effectiveViewType = computed<PassportBuilderFieldLayoutViewType>(() =>
  props.mode === 'reference' && !isValueOnlyReference.value ? viewType.value : 'value',
)

/** Флаги фильтра/поиска имеют смысл только для скалярного размещения. */
const showFilterFlags = computed(() => effectiveViewType.value !== 'table')

/** Справочник обязателен, когда включён хотя бы один из флагов. */
const showDictionarySelect = computed(() => filterMap.value || filterSearch.value)

/** При включённом флаге нужны и pseudo_sql, и связанный справочник. */
const canSave = computed(() => {
  if (!showDictionarySelect.value) return true
  return Boolean(pseudoSql.value.trim()) && filterDictionaryId.value != null
})

/** Скрытие блока флагов (режим «таблица») сбрасывает их значения. */
watch(showFilterFlags, (visible) => {
  if (visible) return
  filterMap.value = false
  filterSearch.value = false
  filterDictionaryId.value = null
})

watch(
  () => props.modelValue,
  (value) => {
    if (!value) return
    pseudoSql.value = props.initialValue?.pseudo_sql ?? ''
    viewType.value = isValueOnlyReference.value ? 'value' : props.initialValue?.view_type ?? 'value'
    filterMap.value = Boolean(props.initialValue?.filter_map)
    filterSearch.value = Boolean(props.initialValue?.filter_search)
    filterDictionaryId.value = props.initialValue?.filter_dictionary_id ?? null
  },
)

/**
 * Вставляет отступ в textarea вместо перехода фокуса на следующее поле.
 */
function insertPseudoSqlIndent(event: KeyboardEvent) {
  const target = event.target as HTMLTextAreaElement | null
  if (!target || typeof target.selectionStart !== 'number') return

  const start = target.selectionStart
  const end = target.selectionEnd
  const indent = '    '
  pseudoSql.value = `${pseudoSql.value.slice(0, start)}${indent}${pseudoSql.value.slice(end)}`

  nextTick(() => {
    target.setSelectionRange(start + indent.length, start + indent.length)
  })
}

/**
 * Возвращает выбранные настройки отображения поля.
 */
function save() {
  emit('save', {
    view_type: effectiveViewType.value,
    pseudo_sql: pseudoSql.value,
    filter_map: filterMap.value,
    filter_search: filterSearch.value,
    filter_dictionary_id: filterDictionaryId.value,
  })
  open.value = false
}
</script>

<template>
  <VModal v-model:show="open" :title="$t('itf.passport_builder_field_layout_title')" :width="560">
    <div class="pb-field-layout-modal">
      <div v-if="fieldInfo" class="pb-field-layout-modal__field-info text-body-2">
        {{ fieldInfo }}
      </div>

      <VSelect
        v-if="mode === 'reference'"
        v-model="viewType"
        :items="viewTypeItems"
        item-title="title"
        item-value="value"
        :label="$t('itf.passport_builder_field_layout_view_type')"
        :disabled="isValueOnlyReference"
        hide-details
        class="pb-field-layout-modal__view-type"
      />

      <div class="pb-field-layout-modal__hint text-body-2 text-medium-emphasis">
        {{ pseudoSqlHint }}
      </div>

      <VSqlEditor
        v-model="pseudoSql"
        :label="$t('itf.passport_builder_pseudo_sql_label')"
        :rows="6"
        auto-grow
        @keydown.tab.prevent="insertPseudoSqlIndent"
      />

      <div
        v-if="showFilterFlags"
        class="pb-field-layout-modal__flags"
        role="group"
        :aria-label="$t('itf.field_flags')"
      >
        <label class="pb-field-layout-modal__flag">
          <VCheckbox v-model="filterMap" hide-details density="compact" />
          <span>{{ $t('itf.field_flag_short_filter') }}</span>
        </label>
        <label class="pb-field-layout-modal__flag">
          <VCheckbox v-model="filterSearch" hide-details density="compact" />
          <span>{{ $t('itf.advanced_search') }}</span>
        </label>
      </div>

      <VSelect
        v-if="showDictionarySelect"
        v-model="filterDictionaryId"
        :items="dictionaries"
        item-title="name"
        item-value="id"
        :label="$t('itf.linked_dictionary')"
        clearable
        density="compact"
        variant="outlined"
        hide-details
      />

      <div class="form__btn_right mt-4">
        <VBtn :disabled="!canSave" @click="save">{{ $t('itf.save') }}</VBtn>
        <VBtn color="default" @click="open = false">{{ $t('itf.cancel') }}</VBtn>
      </div>
    </div>
  </VModal>
</template>

<style scoped lang="scss">
.pb-field-layout-modal {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pb-field-layout-modal__field-info {
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
}

.pb-field-layout-modal__view-type {
  margin-top: 4px;
}

.pb-field-layout-modal__hint {
  line-height: 1.4;
}

.pb-field-layout-modal__flags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
}

.pb-field-layout-modal__flag {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}
</style>
